import React, { ReactNode, useRef, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import {
    CounterpartyFormFields,
    FormSchema,
} from "@components/counterparty/CounterpartyForm";
import { Drawer } from "@design-system/Drawer/Drawer";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { COUNTERPARTY_EDITOR_CREATE_FORM_ID } from "./counterpartyEditorFormIds";
import { CounterpartyEditorCommandBar } from "./CounterpartyEditorCommandBar";
import { CounterpartyEditorDrawerCreate } from "./CounterpartyEditorDrawerCreate";
import { CounterpartyEditorDrawerEdit } from "./CounterpartyEditorDrawerEdit";
import styles from "./CounterpartyEditorDrawer.module.scss";

interface Props {
    open: boolean;
    isCreating: boolean;
    counterparty?: Counterparty;
    onClose: () => void;
    onCreated: (response: CounterpartyCreationResponse) => void;
    onEdited: (counterparty: Counterparty) => void;
    onLocalized: (counterparty: Counterparty) => void;
    initialValues?: Partial<FormSchema>;
    seedContacts?: CreateCounterpartyContactBody[];
    seedAddresses?: CreateCounterpartyAddressBody[];
    seedContactReplacesIds?: ReadonlyArray<string | null>;
    seedAddressReplacesIds?: ReadonlyArray<string | null>;
    removedContactIds?: readonly string[];
    removedAddressIds?: readonly string[];
    fields?: CounterpartyFormFields;
    forTransactionEditing?: boolean;
    direction?: "left" | "right";
    sidePreview?: ReactNode;
}

export const CounterpartyEditorDrawer: React.FC<Props> = ({
    open,
    isCreating,
    counterparty,
    onClose,
    onCreated,
    onEdited,
    onLocalized,
    initialValues,
    seedContacts,
    seedAddresses,
    seedContactReplacesIds,
    seedAddressReplacesIds,
    removedContactIds,
    removedAddressIds,
    fields,
    forTransactionEditing,
    direction = "right",
    sidePreview,
}) => {
    const companyDirtyRef = useRef(false);
    const [isChildEditing, setIsChildEditing] = useState(false);
    const [isDiscardOpen, setIsDiscardOpen] = useState(false);

    const title = isCreating ? "Create counterparty" : "Edit counterparty";

    const finishClose = () => {
        companyDirtyRef.current = false;
        setIsChildEditing(false);
        setIsDiscardOpen(false);
        onClose();
    };

    const requestClose = () => {
        if (companyDirtyRef.current || isChildEditing) {
            setIsDiscardOpen(true);
            return;
        }
        finishClose();
    };

    const handleCloseAttempt = (cancel: () => void) => {
        if (companyDirtyRef.current || isChildEditing) {
            cancel();
            setIsDiscardOpen(true);
        }
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={finishClose}
                onCloseAttempt={handleCloseAttempt}
                direction={direction}
                size={sidePreview ? "fullScreen" : "half"}
                noPadding
                noScroll
                headerAddonLeft={
                    <h2 className={styles.headerTitle}>{title}</h2>
                }
            >
                <div className={sidePreview ? styles.split : styles.shell}>
                    {sidePreview && open && (
                        <div className={styles.sidePreview}>{sidePreview}</div>
                    )}
                    <div className={styles.shell}>
                        {open &&
                            (isCreating ? (
                                <>
                                    <div className={styles.scrollBody}>
                                        <CounterpartyEditorDrawerCreate
                                            onCreated={onCreated}
                                            onCancelled={requestClose}
                                            formId={
                                                COUNTERPARTY_EDITOR_CREATE_FORM_ID
                                            }
                                            dirtyRef={companyDirtyRef}
                                            onChildEditingChange={
                                                setIsChildEditing
                                            }
                                            initialValues={initialValues}
                                            seedContacts={seedContacts}
                                            seedAddresses={seedAddresses}
                                            fields={fields}
                                            forTransactionEditing={
                                                forTransactionEditing
                                            }
                                        />
                                    </div>
                                    <CounterpartyEditorCommandBar
                                        submitLabel="Add"
                                        formId={
                                            COUNTERPARTY_EDITOR_CREATE_FORM_ID
                                        }
                                        onCancel={requestClose}
                                    />
                                </>
                            ) : (
                                counterparty && (
                                    <CounterpartyEditorDrawerEdit
                                        counterparty={counterparty}
                                        companyDirtyRef={companyDirtyRef}
                                        onEdited={onEdited}
                                        onClose={requestClose}
                                        onLocalized={onLocalized}
                                        onChildEditingChange={setIsChildEditing}
                                        seedContacts={seedContacts}
                                        seedAddresses={seedAddresses}
                                        seedContactReplacesIds={
                                            seedContactReplacesIds
                                        }
                                        seedAddressReplacesIds={
                                            seedAddressReplacesIds
                                        }
                                        removedContactIds={removedContactIds}
                                        removedAddressIds={removedAddressIds}
                                    />
                                )
                            ))}
                    </div>
                </div>
            </Drawer>
            <ConfirmationModal
                show={isDiscardOpen}
                title="Discard unsaved changes?"
                question="You have unsaved company details or an open contact or address form."
                yes="Discard"
                yesBtnVariant="destructive"
                onConfirm={finishClose}
                onHide={() => setIsDiscardOpen(false)}
            />
        </>
    );
};
